import React, { useState, useEffect } from 'react';
import { broadbandAPI, catvAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Wifi, Tv, Plus, Edit2, Trash2 } from 'lucide-react';

function PlanModal({ plan, onClose, onSuccess }) {
  const [form, setForm] = useState(plan || { name: '', displayName: '', speed: { download: '', upload: '' }, price: '', validityDays: 30, features: [], isActive: true });
  const [loading, setLoading] = useState(false);
  const [featInput, setFeatInput] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (plan?._id) await broadbandAPI.updatePlan(plan._id, form);
      else await broadbandAPI.createPlan(form);
      toast.success(plan ? 'Plan updated' : 'Plan created');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const addFeature = () => { if (featInput.trim()) { setForm(f => ({ ...f, features: [...f.features, featInput.trim()] })); setFeatInput(''); } };
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{plan ? 'Edit Plan' : 'New Broadband Plan'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Plan Code *</label>
                <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="BB_100_UNLIMITED" />
              </div>
              <div className="form-group">
                <label className="form-label">Display Name *</label>
                <input className="form-control" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} required placeholder="100 Mbps Unlimited" />
              </div>
            </div>
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">Download (Mbps) *</label>
                <input type="number" className="form-control" value={form.speed.download} onChange={e => setForm(f => ({ ...f, speed: { ...f.speed, download: e.target.value } }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Upload (Mbps) *</label>
                <input type="number" className="form-control" value={form.speed.upload} onChange={e => setForm(f => ({ ...f, speed: { ...f.speed, upload: e.target.value } }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Price (₹/mo) *</label>
                <input type="number" className="form-control" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Validity (days)</label>
              <input type="number" className="form-control" value={form.validityDays} onChange={e => setForm(f => ({ ...f, validityDays: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Features</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="form-control" value={featInput} onChange={e => setFeatInput(e.target.value)} placeholder="Add feature..." onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addFeature())} />
                <button type="button" className="btn btn-secondary" onClick={addFeature}>Add</button>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {form.features.map((f, i) => (
                  <span key={i} style={{ background: 'var(--primary-light)', color: 'var(--primary)', padding: '3px 10px', borderRadius: 20, fontSize: '.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {f} <button type="button" onClick={() => removeFeature(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '.9rem', lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} /> Active (visible to staff)
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Plan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CATVModal({ pkg, onClose, onSuccess }) {
  const [form, setForm] = useState(pkg || { name: '', displayName: '', packageType: 'basic', price: '', validityDays: 30, channelCount: 0, hasIPTV: false, isActive: true });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (pkg?._id) await catvAPI.updatePackage(pkg._id, form);
      else await catvAPI.createPackage(form);
      toast.success(pkg ? 'Package updated' : 'Package created');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{pkg ? 'Edit Package' : 'New CATV Package'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Code *</label>
                <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Display Name *</label>
                <input className="form-control" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} required />
              </div>
            </div>
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-control" value={form.packageType} onChange={e => setForm(f => ({ ...f, packageType: e.target.value }))}>
                  <option value="basic">Basic</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="sports">Sports</option>
                  <option value="iptv">IPTV</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Price (₹/mo) *</label>
                <input type="number" className="form-control" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Channels</label>
                <input type="number" className="form-control" value={form.channelCount} onChange={e => setForm(f => ({ ...f, channelCount: e.target.value }))} />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer', marginBottom: 12 }}>
              <input type="checkbox" checked={form.hasIPTV} onChange={e => setForm(f => ({ ...f, hasIPTV: e.target.checked }))} /> Includes IPTV
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} /> Active
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Package'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [tab, setTab] = useState('broadband');
  const [modal, setModal] = useState(null); // { type: 'bb'|'catv', item?: plan }
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pb, pc] = await Promise.all([
        broadbandAPI.getPlans({ activeOnly: 'false' }),
        catvAPI.getPackages({ activeOnly: 'false' })
      ]);
      setPlans(pb.data.data);
      setPackages(pc.data.data);
    } catch { toast.error('Failed to load plans'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Plans & Packages</h2>
          <p className="text-muted text-small">Manage broadband plans and CATV packages</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({ type: tab === 'broadband' ? 'bb' : 'catv' })}>
          <Plus size={15} /> New {tab === 'broadband' ? 'Plan' : 'Package'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '2px solid var(--border)' }}>
        {[{ key: 'broadband', label: '🌐 Broadband Plans', count: plans.length }, { key: 'catv', label: '📺 CATV Packages', count: packages.length }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === t.key ? 600 : 400,
            color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)',
            borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -2, fontSize: '.9rem'
          }}>
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {tab === 'broadband' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {plans.map(p => (
            <div key={p._id} className="card" style={{ opacity: p.isActive ? 1 : 0.55 }}>
              <div className="card-header">
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{p.displayName}</div>
                  <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }} className="font-mono">{p.name}</div>
                </div>
                <div className="flex gap-2">
                  <button className="btn btn-secondary btn-sm" onClick={() => setModal({ type: 'bb', item: p })}><Edit2 size={12} /></button>
                </div>
              </div>
              <div className="card-body">
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)', marginBottom: 8 }}>
                  ₹{p.price}<span style={{ fontSize: '.85rem', fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span>
                </div>
                <div style={{ fontSize: '.9rem', color: 'var(--text)', marginBottom: 8, fontWeight: 600 }}>
                  ⬇ {p.speed?.download} / ⬆ {p.speed?.upload} Mbps
                </div>
                <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                  {p.validityDays} days validity
                </div>
                {p.features?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {p.features.map((f, i) => (
                      <span key={i} style={{ background: 'var(--primary-light)', color: 'var(--primary)', padding: '2px 8px', borderRadius: 12, fontSize: '.72rem' }}>{f}</span>
                    ))}
                  </div>
                )}
                {!p.isActive && <div style={{ marginTop: 8 }}><span className="badge badge-warning">Inactive</span></div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'catv' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {packages.map(p => (
            <div key={p._id} className="card" style={{ opacity: p.isActive ? 1 : 0.55 }}>
              <div className="card-header">
                <div>
                  <div style={{ fontWeight: 700 }}>{p.displayName}</div>
                  <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{p.packageType}</div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setModal({ type: 'catv', item: p })}><Edit2 size={12} /></button>
              </div>
              <div className="card-body">
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#7c3aed', marginBottom: 8 }}>
                  ₹{p.price}<span style={{ fontSize: '.85rem', fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span>
                </div>
                <div style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>
                  {p.channelCount} channels · {p.validityDays} days
                  {p.hasIPTV && <span style={{ marginLeft: 8, color: 'var(--accent)', fontWeight: 600 }}>+ IPTV</span>}
                </div>
                {!p.isActive && <div style={{ marginTop: 8 }}><span className="badge badge-warning">Inactive</span></div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal?.type === 'bb' && (
        <PlanModal plan={modal.item} onClose={() => setModal(null)} onSuccess={() => { setModal(null); loadAll(); }} />
      )}
      {modal?.type === 'catv' && (
        <CATVModal pkg={modal.item} onClose={() => setModal(null)} onSuccess={() => { setModal(null); loadAll(); }} />
      )}
    </div>
  );
}
